import { GetStaticProps, GetStaticPaths } from 'next';
import { getPostBySlug, getAllPosts } from '../../lib/posts';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import remarkGfm from 'remark-gfm';
import Head from 'next/head';
import { formatDate } from '@/lib/blog_utils';
import { Markdown } from '@/components/ui/Messages/Markdown';
import PageMetaContext from '@/lib/context/metaContext';
import { NextSeo } from 'next-seo';
import { MDXComponent, getCompiledMdx } from '@mintlify/mdx';
import type { MDXCompiledResult } from '@mintlify/mdx';
import Banner from '@/components/Banner';
import HowItWorks from '@/components/app/tools/HowItWorks';
import dynamic from 'next/dynamic';
import { authors } from '../../lib/authors';
const components = {
  HowItWorks: dynamic(() => import('@/components/app/tools/HowItWorks')),
  MendableActions: dynamic(() => import('@/components/MendableActions')),
  GrabIcon: dynamic(() => import('lucide-react').then((mod) => mod.GrabIcon)),
};

export default function Post({
  post,
  pageMeta,
  mdxSource
}: {
  post: any;
  pageMeta: any;
  mdxSource: MDXCompiledResult;
}) {
  return (
    <>
      <NextSeo
        title={post.title}
        description={post.subheadline}
        openGraph={{
          title: post.title,
          description: post.subheadline,
          images: [
            {
              url: post.featuredImage,
              width: 800,
              height: 600,
              alt: post.title + ' image'
            }
          ]
        }}
        twitter={{
          handle: '@mendableai',
          site: '@mendableai',
          cardType: 'summary_large_image'
        }}
      />
      <Head>
        <meta property="og:image" content={`https://www.mendable.ai/${post.featuredImage}`} />
        <meta property="og:image:secure_url" content={`https://www.mendable.ai/${post.featuredImage}`} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="600" />
        <meta property="og:image:alt" content={post.title + ' image'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={"Mendable | " + post.title} />
        {/* twitter:image */}

        {/* // card */}
        <meta name="twitter:description" content={post.subheadline} />
        <meta name="twitter:image" content={`https://www.mendable.ai/${post.featuredImage}`} />

        {/* title */}
      </Head>

      <PageMetaContext.Provider value={pageMeta}>
        <section className="relative mt-28  text-white flex justify-center items-center">
          <div className="flex flex-col justify-center items-center">
            <div className="max-w-7xl mx-auto text-center mt-10">
              <div className="flex justify-center items-center  gap-2 sm:gap-5 mb-4 sm:mb-8  text-xs sm:text-sm text-gray-300">
              <p className="text-xs sm:text-sm text-gray-300 ">
                  {formatDate(post.date)}
                </p>
                <p className="text-xs sm:text-sm text-gray-300 ">
                  •
                </p>
                <a href={`https://x.com/${authors[post.author].handle}`} target="_blank" rel="noopener noreferrer" className="flex justify-center items-center rounded-lg">
                  <span className="mr-2 relative block flex-shrink-0 overflow-hidden rounded-full">
                    <img
                      src={authors[post.author].img}
                      className="border border-primary border-opacity-50 rounded-full"
                      alt={authors[post.author].name + ' image'}
                      width="28"
                      height="28"
                    />
                  </span>
                  <span className="  ">
                    {authors[post.author].name}
                  </span>
                </a>
                
              </div>
              <h1 className="text-3xl sm:text-5xl font-bold sm:mb-4 text-center max-w-3xl">
                {post.title}
              </h1>
              <p className="mt-2 text-base sm:text-lg max-w-xs mx-auto sm:max-w-3xl text-center text-gray-300 sm:mb-8">
                {post.subheadline}
              </p>
            </div>
            <img
              src={post.featuredImage}
              className="mx-auto w-full sm:w-[775px] rounded-md p-4 sm:p-0"
              alt={post.title + ' image'}
            />
            <div className="max-w-7xl mx-auto text-center sm:mt-10">
              <div className="prose prose-pre:whitespace-pre-wrap prose-pre:break-words prose-code:whitespace-pre-wrap prose-code:break-words sm:max-w-[700px] prose-invert mx-auto text-left prose-p:text-gray-300 prose-ul:text-gray-300 prose-li:text-gray-300 p-4 sm:p-0 text-base prose-h2:text-base prose-h2:sm:text-2xl">
                {/* <Markdown
                  str={post.content}
                  remarkPlugins={[remarkGfm]}
                ></Markdown> */}
                <MDXComponent {...mdxSource} components={components} />
              </div>
            </div>
          </div>
        </section>
      </PageMetaContext.Provider>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const post = getPostBySlug(params?.slug, [
    'title',
    'subheadline',
    'date',
    'content',
    'featuredImage',
    'author',
  ]);
  const mdxSource = await getCompiledMdx({
    source: post.content
  });
  return {
    props: {
      post,
      pageMeta: {
        title: post.title,
        description: post.subheadline,
        ogImage: post.featuredImage,
        author: post.author,
      },
      mdxSource: mdxSource
    }
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = getAllPosts(['slug']);
  return {
    paths: posts.map((post) => {
      return {
        params: {
          slug: post.slug
        }
      };
    }),
    fallback: false
  };
};
